/**
 * The half of the margin that lives inside the text: the highlight under an
 * anchored comment, and the click that selects its thread.
 *
 * A ProseMirror plugin rather than anything React draws on top, because the
 * thing being marked is a character range in a document that is being typed
 * into. Decorations are mapped through every transaction, so a highlight follows
 * its words as a co-author edits around them — an overlay positioned from
 * `coordsAtPos` would be correct until the first keystroke above it.
 *
 * **It is registered, not configured in.** `document-editor.tsx` builds the
 * extension list from `extensions.ts`, and `roundtrip.test.ts` asserts that list
 * adds no node and no mark; a decoration plugin adds neither, but it also has no
 * business in a list the API and the collab server build their schema from. So
 * the comment layer attaches it to the live editor with `registerPlugin` and
 * removes it on unmount, which keeps the schema exactly where §5.1 wants it and
 * keeps this feature inside its own directory.
 *
 * The plugin holds no data of its own. Threads arrive on a transaction's meta,
 * because the source of truth is a TanStack query in React and a plugin that
 * fetched would be a second one.
 */

import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { type BlockAnchor, findBlock, locateAnchor } from './anchor.ts';

export interface CommentHighlight {
  commentId: string;
  anchor: BlockAnchor;
  /** §8.1 — the audiences differ, so the marks differ. */
  internal: boolean;
  /** §8.2 — a suggestion marks text it proposes to replace. */
  suggestion: boolean;
}

interface HighlightPayload {
  highlights: readonly CommentHighlight[];
  selectedId: string | null;
}

interface DecorationSpec {
  commentId: string;
}

export const commentHighlightKey = new PluginKey<DecorationSet>('matchboxCommentHighlights');

function classesFor(highlight: CommentHighlight, selected: boolean): string {
  const classes = ['mx-comment-mark'];
  classes.push(highlight.internal ? 'mx-comment-mark-internal' : 'mx-comment-mark-external');
  if (highlight.suggestion) classes.push('mx-comment-mark-suggestion');
  if (selected) classes.push('mx-comment-mark-selected');
  return classes.join(' ');
}

function build(doc: PMNode, payload: HighlightPayload): DecorationSet {
  const decorations: Decoration[] = [];

  for (const highlight of payload.highlights) {
    const attrs = {
      class: classesFor(highlight, highlight.commentId === payload.selectedId),
      'data-comment-thread': highlight.commentId,
    };
    const spec: DecorationSpec = { commentId: highlight.commentId };

    // A block-level anchor has no range to underline — it points at the block,
    // which is what a comment on an image or on a whole paragraph means. Marking
    // the node is the only way to show one; an inline decoration of zero width
    // renders nothing at all, which would make those comments invisible in the
    // text while the panel insisted they were anchored.
    if (highlight.anchor.text.length === 0) {
      const block = findBlock(doc, highlight.anchor.blockId);
      if (!block) continue;
      decorations.push(Decoration.node(block.pos, block.pos + block.node.nodeSize, attrs, spec));
      continue;
    }

    const range = locateAnchor(doc, highlight.anchor);
    if (!range || range.to <= range.from) continue;
    decorations.push(Decoration.inline(range.from, range.to, attrs, spec));
  }

  return DecorationSet.create(doc, decorations);
}

export interface HighlightPluginOptions {
  /**
   * Called when a reader clicks marked text. Read through a getter so the plugin
   * can be registered once per editor while the handler follows React's state —
   * a handler captured at registration would go on selecting whatever thread was
   * open when the editor was built.
   */
  onSelect: (commentId: string) => void;
}

export function commentHighlightPlugin(options: HighlightPluginOptions): Plugin {
  return new Plugin<DecorationSet>({
    key: commentHighlightKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, value) {
        const payload = tr.getMeta(commentHighlightKey) as HighlightPayload | undefined;
        if (payload) return build(tr.doc, payload);
        // No new data: carry the marks through the edit. This is why the
        // highlight stays on the right words while somebody types above it, and
        // why deleting the marked text takes its highlight with it — several
        // seconds before the server's sweep records the orphan (§8.1).
        return value.map(tr.mapping, tr.doc);
      },
    },
    props: {
      decorations: (state) => commentHighlightKey.getState(state) ?? DecorationSet.empty,
      handleClick(view, pos) {
        const set = commentHighlightKey.getState(view.state);
        const hit = set?.find(pos, pos) ?? [];
        const spec = hit.find((decoration) => 'commentId' in decoration.spec)?.spec as
          | DecorationSpec
          | undefined;
        if (!spec) return false;
        options.onSelect(spec.commentId);
        // Handled, but not consumed: the click still places the cursor, because
        // opening a thread should not stop someone editing the sentence it is
        // about.
        return false;
      },
    },
  });
}

/** Hands the plugin a fresh set of threads. A no-op on an editor being torn down. */
export function setHighlights(
  editor: Editor,
  highlights: readonly CommentHighlight[],
  selectedId: string | null,
): void {
  if (editor.isDestroyed) return;
  const payload: HighlightPayload = { highlights, selectedId };
  // `setMeta` alone: no document change, so this never reaches Yjs and never
  // marks the editor dirty. A decoration that produced a `yjs_update` row would
  // be a margin rendering that shows up in somebody's version history.
  editor.view.dispatch(editor.state.tr.setMeta(commentHighlightKey, payload));
}

/**
 * Scrolls a thread's text into view and flashes it.
 *
 * Through the DOM rather than through a selection: on a read-only surface — a
 * client reading a published deliverable, which is precisely who §8.1's second
 * half is for — setting a text selection paints nothing, and moving an editor's
 * selection to answer a click in a list would fight the caret of whoever is
 * typing.
 */
export function scrollToThread(editor: Editor, commentId: string): void {
  if (editor.isDestroyed) return;
  const selector = `[data-comment-thread="${CSS.escape(commentId)}"]`;
  const element = editor.view.dom.querySelector(selector);
  element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}
